import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Save, ExternalLink, FolderPlus, GripVertical, MoveRight, ArrowLeft, Link2 } from 'lucide-react';
import AdminNav from '../components/AdminNav';
import ImageUpload from '../components/ImageUpload';
import { useLanguage } from '../i18n/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...options });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

// ── Category Manager Inline ────────────────────
const CategoryManager = ({ categories, onRefresh, t }) => {
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await apiFetch('/api/capabilities/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() })
      });
      setNewName('');
      onRefresh();
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  const handleRename = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await apiFetch(`/api/capabilities/categories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim() })
      });
      setEditId(null);
      onRefresh();
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Capabilities will move to "Other".`)) return;
    try {
      await apiFetch(`/api/capabilities/categories/${cat.id}`, { method: 'DELETE' });
      onRefresh();
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="category-manager">
      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('admin.capabilities.manageCategories') || 'Manage Categories'}</h3>
      <div className="space-y-1.5">
        {categories.map(cat => (
          <div key={cat.id} className="flex items-center gap-2 group" data-testid={`cat-row-${cat.id}`}>
            <GripVertical className="w-3.5 h-3.5 text-slate-300" />
            {editId === cat.id ? (
              <>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleRename(cat.id)}
                  className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid={`cat-rename-input-${cat.id}`} autoFocus />
                <button onClick={() => handleRename(cat.id)} disabled={saving}
                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`cat-rename-save-${cat.id}`}>
                  <Save className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setEditId(null)} className="p-1 text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-slate-700">{cat.name}</span>
                <button onClick={() => { setEditId(cat.id); setEditName(cat.name); }}
                  className="p-1 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`cat-edit-${cat.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(cat)}
                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`cat-delete-${cat.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New category name..."
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          className="flex-1 px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30"
          data-testid="new-category-input" />
        <button onClick={handleCreate} disabled={saving || !newName.trim()} data-testid="create-category-btn"
          className="flex items-center gap-1 px-3 py-1.5 bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg">
          <FolderPlus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
};

// ── Move Category Dialog ───────────────────────
const MoveDialog = ({ cap, categories, onMove, onClose }) => {
  const [target, setTarget] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 p-5 w-80 shadow-xl" onClick={e => e.stopPropagation()} data-testid="move-dialog">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Move "{cap.name}" to:</h3>
        <select value={target} onChange={e => setTarget(e.target.value)} data-testid="move-target-select"
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm mb-3">
          <option value="">Select category...</option>
          {categories.filter(c => c.slug !== cap.category).map(c => (
            <option key={c.id} value={c.slug}>{c.name}</option>
          ))}
        </select>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700">Cancel</button>
          <button onClick={() => { if (target) onMove(cap.id, target); }}
            disabled={!target} data-testid="move-confirm-btn"
            className="px-4 py-1.5 bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg">
            Move
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────
const CapabilitiesManagerPage = () => {
  const { t } = useLanguage();
  const [capabilities, setCapabilities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCategories, setShowCategories] = useState(false);
  const [movingCap, setMovingCap] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', default_image_url: '', default_destination_url: '', category: '' });

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [capData, catData] = await Promise.all([
        apiFetch('/api/capabilities'),
        apiFetch('/api/capabilities/categories')
      ]);
      setCapabilities(capData.items || []);
      setCategories(catData.items || []);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startEdit = (cap) => {
    setEditing(cap?.id || 'new');
    setForm(cap ? {
      name: cap.name,
      description: cap.description || '',
      default_image_url: cap.default_image_url || '',
      default_destination_url: cap.default_destination_url || '',
      category: cap.category || (categories[0]?.slug || '')
    } : {
      name: '',
      description: '',
      default_image_url: '',
      default_destination_url: '',
      category: categories[0]?.slug || ''
    });
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (editing === 'new') {
        await apiFetch('/api/capabilities', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      } else {
        await apiFetch(`/api/capabilities/${editing}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      }
      setEditing(null); fetchAll();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (cap) => {
    if (!window.confirm(`Delete capability "${cap.name}"?`)) return;
    try { await apiFetch(`/api/capabilities/${cap.id}`, { method: 'DELETE' }); fetchAll(); } catch (err) { setError(err.message); }
  };

  const handleMove = async (capId, newCategory) => {
    try {
      await apiFetch(`/api/capabilities/${capId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: newCategory })
      });
      setMovingCap(null);
      fetchAll();
    } catch (err) { setError(err.message); }
  };

  const handleSeeLink = (url) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Group by category slugs
  const catSlugs = categories.map(c => c.slug);
  const catMap = Object.fromEntries(categories.map(c => [c.slug, c.name]));
  const grouped = {};
  catSlugs.forEach(s => { grouped[s] = capabilities.filter(c => c.category === s); });
  // Capabilities with unknown categories go to "other"
  const knownSlugs = new Set(catSlugs);
  const orphans = capabilities.filter(c => !knownSlugs.has(c.category));
  if (orphans.length > 0) {
    grouped['other'] = [...(grouped['other'] || []), ...orphans];
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="capabilities-manager-page">
      <AdminNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {error} <button onClick={() => setError('')} className="float-right"><X className="w-4 h-4" /></button>
          </div>
        )}

        {movingCap && <MoveDialog cap={movingCap} categories={categories} onMove={handleMove} onClose={() => setMovingCap(null)} />}

        {editing !== null ? (
          <div className="space-y-4" data-testid="capability-editor">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setEditing(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                <h1 className="text-xl font-bold text-slate-900">{editing === 'new' ? (t('admin.capabilities.new') || 'New Capability') : (t('admin.capabilities.edit') || 'Edit Capability')}</h1>
              </div>
              <button onClick={handleSave} disabled={saving || !form.name} data-testid="save-capability-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl">
                <Save className="w-4 h-4" /> {saving ? (t('admin.common.saving') || 'Saving...') : (t('admin.common.save') || 'Save')}
              </button>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.capabilities.name') || 'Name'}</label>
                  <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" data-testid="cap-name-input" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.capabilities.category') || 'Category'}</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" data-testid="cap-category-select">
                    {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.capabilities.description') || 'Description'}</label>
                <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" data-testid="cap-desc-input" />
              </div>
              <div>
                <ImageUpload value={form.default_image_url} onChange={v => setForm(p => ({ ...p, default_image_url: v }))}
                  label={t('admin.capabilities.defaultImage') || 'Default Card Image'} testId="cap-image" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.capabilities.defaultDestination') || 'Default Destination URL'}</label>
                <div className="flex gap-2">
                  <input value={form.default_destination_url} onChange={e => setForm(p => ({ ...p, default_destination_url: e.target.value }))}
                    placeholder="https://..." className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" data-testid="cap-destination-input" />
                  {form.default_destination_url && (
                    <button onClick={() => handleSeeLink(form.default_destination_url)}
                      className="px-3 py-2 text-[#0077FF] hover:bg-blue-50 border border-slate-200 rounded-lg text-xs font-semibold"
                      data-testid="cap-see-link-editor">
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="capabilities-list">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-slate-900">{t('admin.capabilities.title') || 'Capability Library'}</h1>
                <p className="text-sm text-slate-500 mt-0.5">{t('admin.capabilities.subtitle') || 'Manage reusable capabilities'}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowCategories(!showCategories)} data-testid="toggle-categories-btn"
                  className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-xl hover:bg-slate-50">
                  <FolderPlus className="w-4 h-4" /> {t('admin.capabilities.categories') || 'Categories'}
                </button>
                <button onClick={() => startEdit(null)} data-testid="create-capability-btn"
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] text-white text-sm font-semibold rounded-xl">
                  <Plus className="w-4 h-4" /> {t('admin.capabilities.new') || 'New Capability'}
                </button>
              </div>
            </div>

            {showCategories && <CategoryManager categories={categories} onRefresh={fetchAll} t={t} />}

            {loading ? <div className="text-center py-16 text-slate-400">{t('admin.common.loading') || 'Loading...'}</div> : capabilities.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl"><p className="text-slate-400 text-sm">{t('admin.common.noItems') || 'No items'}</p></div>
            ) : (
              <div className="space-y-5">
                {catSlugs.map(slug => {
                  const items = grouped[slug];
                  if (!items || items.length === 0) return null;
                  return (
                    <div key={slug} data-testid={`cat-group-${slug}`}>
                      <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">{catMap[slug] || slug}</h2>
                      <div className="space-y-1.5">
                        {items.map(cap => {
                          const hasLink = !!cap.default_destination_url;
                          return (
                            <div key={cap.id} className="bg-white border border-slate-200 rounded-xl px-5 py-3 flex items-center justify-between group" data-testid={`cap-row-${cap.id}`}>
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className={`w-2 h-2 rounded-full shrink-0 ${hasLink ? 'bg-emerald-500' : 'bg-red-400'}`} title={hasLink ? 'Link configured' : 'No link configured'} data-testid={`cap-link-status-${cap.id}`} />
                                <div className="min-w-0">
                                  <p className="font-semibold text-slate-800 text-sm">{cap.name}</p>
                                  <p className="text-xs text-slate-400 mt-0.5 truncate">{cap.description || 'No description'}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-0.5 shrink-0">
                                {hasLink ? (
                                  <button onClick={() => handleSeeLink(cap.default_destination_url)} title="See Link"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                    data-testid={`cap-see-link-${cap.id}`}>
                                    <ExternalLink className="w-3.5 h-3.5" /> See Link
                                  </button>
                                ) : (
                                  <button onClick={() => startEdit(cap)} title="Configure Link"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                                    data-testid={`cap-set-link-${cap.id}`}>
                                    <Link2 className="w-3.5 h-3.5" /> Set Link
                                  </button>
                                )}
                                <button onClick={() => setMovingCap(cap)} title="Move to category"
                                  className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                  data-testid={`cap-move-${cap.id}`}>
                                  <MoveRight className="w-4 h-4" />
                                </button>
                                <button onClick={() => startEdit(cap)}
                                  className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                                  data-testid={`cap-edit-${cap.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDelete(cap)}
                                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                  data-testid={`cap-delete-${cap.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CapabilitiesManagerPage;
